/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOnMatchFound';
import { getManager } from '../../registry/Manager';

const ActionOnMatchFoundManager: ActionManagerFn<'ActionOnMatchFound'> = (
  platform: Platform,
): ActionManager<'ActionOnMatchFound'> => {
  return {
    ...baseActionManager(platform, 'ActionOnMatchFound'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    replaceableWith: ['ActionOnMatchNotFound'],
    name: 'On Match Found',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return parent.type === 'ActionMatchRecord';
    },
    description: `**Overview**<br/>
The ActionOnMatchFound component is a specialized logic container designed solely to handle the "Success" outcome of a Match Record search. It functions as the specific branch of execution that runs if (and only if) the parent action successfully identifies a record that meets the defined criteria.<br/><br/>
This component is structurally dependent on ActionMatchRecord and cannot exist independently. It serves as a wrapper, allowing developers to define exactly what should happen when a duplicate is found, a validation passes, or a specific item exists in the store.

**Key Features**<br/>
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only as a child of ActionMatchRecord.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed), enabling complex workflows upon a successful match.
* **Component Interchangeability:** Configured to be replaceable with ActionOnMatchNotFound, allowing developers to easily swap logic branches if the requirement changes.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Duplicate Prevention:** Showing an error message ("This email is already taken") when a "Match Record" search finds an existing user account.
* **Status Updates:** Triggering an update action to change a status to "Active" only if a prior check confirms the parent record exists.`,
  };
};

export default ActionOnMatchFoundManager;
